// public/js/features/assets/field-labels.js
// Per-tenant display labels for the asset identification fields. Only the UI
// label changes — the stored field names (label/reference/serial/type) stay the
// same, so queries, dedup and import are unaffected (pure white-labeling).
//
// A tenant may set tenant.assetFieldLabels = { label, reference, serial, type };
// any missing key falls back to the built-in default below.

// Built-in free-text identification fields. 'type' (and other dropdowns) are
// handled by the asset-field registry (asset-fields.js), not here.
export const DEFAULT_ASSET_FIELD_LABELS = {
  label:     'Label',
  reference: 'Reference',
  serial:    'Serial',
};

const FIELD_KEYS = Object.keys(DEFAULT_ASSET_FIELD_LABELS);

// Merge a tenant's custom labels over the defaults → a complete label map.
export function resolveFieldLabels(tenant) {
  const custom = (tenant && tenant.assetFieldLabels) || {};
  const out = { ...DEFAULT_ASSET_FIELD_LABELS };
  for (const k of FIELD_KEYS) {
    if (custom[k] && String(custom[k]).trim()) out[k] = String(custom[k]).trim();
  }
  return out;
}

// Single field label for a tenant (with default fallback).
export function fieldLabel(tenant, key) {
  return resolveFieldLabels(tenant)[key] || DEFAULT_ASSET_FIELD_LABELS[key] || key;
}

export const FIELD_LABEL_KEYS = FIELD_KEYS;

// Two optional, per-tenant free-text custom fields. Unlike the identification
// fields they are OFF by default and get a fully tenant-defined title. They reuse
// the existing tenant maps: title ← assetFieldLabels[key], on/off ←
// assetFieldVisibility[key]. Values are stored on the asset under custom1/custom2.
export const CUSTOM_TEXT_FIELDS = [
  { key: 'custom1', defaultLabel: 'Custom field 1' },
  { key: 'custom2', defaultLabel: 'Custom field 2' },
  { key: 'custom3', defaultLabel: 'Custom field 3' },
  { key: 'custom4', defaultLabel: 'Custom field 4' },
  { key: 'custom5', defaultLabel: 'Custom field 5' },
];

// → [{ key, label, enabled }] for a tenant (label default fallback; enabled = OFF by default).
export function customTextFields(tenant) {
  const labels = (tenant && tenant.assetFieldLabels) || {};
  const vis = (tenant && tenant.assetFieldVisibility) || {};
  return CUSTOM_TEXT_FIELDS.map(f => ({
    key: f.key,
    label: (labels[f.key] && String(labels[f.key]).trim()) || f.defaultLabel,
    enabled: !!vis[f.key],
  }));
}

// FIXED optional free-text fields. Like the custom fields they are OFF by default
// and toggled per tenant (assetFieldVisibility[key]), BUT their key AND label are
// canonical and NOT tenant-renamable — so the same field means the same thing for
// every tenant when data is shared/exported. Values are stored on the asset under
// the field key. Add new canonical fields here.
export const FIXED_TEXT_FIELDS = [
  { key: 'engine_serialNo',              label: 'ESN' },
  { key: 'engine_stand_serialNo',        label: 'Engine stand serial no.' },
  { key: 'engine_stand_cradle_serialNo', label: 'Engine stand cradle serial no.' },
];

// → [{ key, label, enabled }] for a tenant. Label is ALWAYS the canonical label
// (never a tenant override); enabled = OFF by default.
export function fixedTextFields(tenant) {
  const vis = (tenant && tenant.assetFieldVisibility) || {};
  return FIXED_TEXT_FIELDS.map(f => ({ key: f.key, label: f.label, enabled: !!vis[f.key] }));
}
